import React, { createContext, useContext, useEffect, useState } from 'react'

// Mock auth service for now
const mockAuthService = {
  async login(email: string, _password: string) {
    // Mock login - always succeeds
    return {
      user: { id: '1', email, name: 'Test User' },
      token: 'mock-token'
    }
  },
  async signup(email: string, _password: string, name: string) {
    // Mock signup - always succeeds
    return {
      user: { id: '1', email, name },
      token: 'mock-token'
    }
  },
  async verifyToken(_token: string) {
    // Mock verification - always succeeds
    return { id: '1', email: 'test@example.com', name: 'Test User' }
  },
  async logout() {
    // Mock logout
  }
}

interface User {
  id: string
  email: string
  name: string
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string, name: string) => Promise<void>
  logout: () => void
  loading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token')
        if (token) {
          const userData = await mockAuthService.verifyToken(token)
          setUser(userData)
        }
      } catch (error) {
        localStorage.removeItem('token')
      } finally {
        setLoading(false)
      }
    }

    initAuth()
  }, [])

  const login = async (email: string, password: string) => {
    const { user: userData, token } = await mockAuthService.login(email, password)
    localStorage.setItem('token', token)
    setUser(userData)
  }

  const signup = async (email: string, password: string, name: string) => {
    const { user: userData, token } = await mockAuthService.signup(email, password, name)
    localStorage.setItem('token', token)
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
  }

  const value = {
    user,
    login,
    signup,
    logout,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
